import { useSiteSettingsContext } from '@core/shared';
import { useMatches } from '@online-webinar/router';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { RouteTitle, TitleName, TitlePage } from './routeTitle';

type Params = Record<string, string>;
type Picked =
    | { kind: 'page'; title: Exclude<RouteTitle, { name: TitleName }>; webinar?: string; params?: Params }
    | { kind: 'name'; title: Extract<RouteTitle, { name: TitleName }>; webinar?: string; params: Params; matchId: string };

const separator = ' | ';

/**
 * The page's <title>, `page | site name`. One for the whole app, rendered in the root: React hoists it into <head>,
 * also in the server HTML. In React rather than in TanStack's `head`, because a language switch re-renders it.
 */
export function DocumentTitle() {
    const { t } = useTranslation();
    const picked = usePickedTitle();

    if (picked.kind === 'name') {
        return <NamedDocumentTitle key={picked.matchId} title={picked.title} params={picked.params} webinar={picked.webinar} />;
    }
    return <TitleElement page={translate(t, picked.title, picked.webinar, picked.params ?? {})} />;
}

function usePickedTitle(): Picked {
    const matches = useMatches();

    // A missing or failed page shows a not-found or error page, whatever the deepest match's title says. An unknown
    // URL (notFoundMode 'root') keeps its deepest partial match, e.g. /login for /login/extra; only `_notFound` on
    // the root says so, its status stays 'success'.
    const deepest = matches[matches.length - 1];
    // Every page route has a title (documentTitleCoverage.test.ts); only the root and layout routes have none.
    const title = deepest?.staticData.title;
    if (!title || matches.some((match) => match._notFound || match.status === 'notFound')) {
        return { kind: 'page', title: { page: 'Title.NotFound' } };
    }
    if (matches.some((match) => match.status === 'error')) {
        return { kind: 'page', title: { page: 'Generic.ApiErrorCode.Exception' } };
    }

    // From the `$webinarId` loader, or a loader of its own (`/w/$linkCode`); the deepest one wins.
    const webinar = matches
        .map((match) => (match.loaderData as { webinar?: { title?: unknown } } | undefined)?.webinar?.title)
        .filter((title): title is string => typeof title === 'string')
        .pop();
    // A webinar page without its webinar shows an error: /w/$linkCode leaves an unknown link to WatchLinkPage.
    if (webinar === undefined && ('webinar' in title || 'webinarTitle' in title)) {
        return { kind: 'page', title: { page: 'Generic.ApiErrorCode.Exception' } };
    }

    const params = deepest.params as Params;
    if ('name' in title) {
        return { kind: 'name', title, webinar, params, matchId: deepest.id };
    }
    return { kind: 'page', title, webinar, params };
}

function NamedDocumentTitle({ title, params, webinar }: Readonly<{ title: Extract<RouteTitle, { name: TitleName }>; params: Params; webinar?: string }>) {
    const { t } = useTranslation();
    // The page fetches the same query; this reads it from the cache and follows its updates.
    const { data } = useQuery(title.name.query(params));
    const name = data === undefined ? undefined : title.name.select(data);
    return <TitleElement page={joinWebinar(t, name ?? t(title.name.fallback), title.forWebinar, webinar)} />;
}

function translate(t: TFunction, title: Exclude<RouteTitle, { name: TitleName }>, webinar: string | undefined, params: Params) {
    if ('webinarTitle' in title) return webinar;
    if ('webinar' in title) return webinar === undefined ? undefined : t(title.webinar, { webinar });
    const page = t(pageKey(title.page, params));
    return joinWebinar(t, title.section ? `${page}${separator}${t(title.section)}` : page, title.forWebinar, webinar);
}

function pageKey(page: TitlePage, params: Params) {
    return typeof page === 'string' ? page : (page.keys[params[page.param]] ?? Object.values(page.keys)[0]);
}

function joinWebinar(t: TFunction, page: string, forWebinar: true | undefined, webinar: string | undefined) {
    return forWebinar && webinar !== undefined ? t('Title.ForWebinar', { page, webinar }) : page;
}

function TitleElement({ page }: Readonly<{ page: string | undefined }>) {
    const site = useSiteSettingsContext().title;
    // One string child: React 19 rejects a <title> with several children.
    return <title>{[page, site].filter(Boolean).join(separator)}</title>;
}
